<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import type { BookPageConfig } from '../configs/types'

interface Props {
  book: any
  config: BookPageConfig
  coverUrl?: string
  popupTOC: boolean
  bannerShow: boolean
  currentProgressChapterId?: string
  unlockedChapterIds?: string[]
}

const props = defineProps<Props>()
const emit = defineEmits<{
  (event: 'update:popup-t-o-c', value: boolean): void
  (event: 'goto-tags', tagId: string): void
  (event: 'open-toc'): void
  (event: 'bookshelf'): void
  (event: 'read'): void
  (event: 'back'): void
  (event: 'cover-loaded'): void
  (event: 'preload-complete', costMs: number): void
  (event: 'reset-confirm', chapterId: string | number | undefined, clearRoleConfig: boolean): void
}>()

// 创建可写的 computed 用于 v-model 双向绑定
const popupTOCModel = computed({
  get: () => props.popupTOC,
  set: value => emit('update:popup-t-o-c', value),
})

// 目录重置模式状态
const restartMode = ref(false)
const confirmVisible = ref(false)
const resetChapterId = ref<string | number | undefined>(undefined)
const resetChapterNo = ref<number | undefined>(undefined)

function openTOC() {
  restartMode.value = false
  popupTOCModel.value = true
}

function openRestart() {
  restartMode.value = true
  popupTOCModel.value = true
}

function onResetChapter(chapterId: string | number, chapterNo: number) {
  popupTOCModel.value = false
  restartMode.value = false
  resetChapterId.value = chapterId
  resetChapterNo.value = chapterNo
  confirmVisible.value = true
}

function onResetAll() {
  popupTOCModel.value = false
  restartMode.value = false
  resetChapterId.value = undefined
  confirmVisible.value = true
}

function handleResetConfirm(chapterId: string | number | undefined, clearRoleConfig: boolean) {
  emit('reset-confirm', chapterId, clearRoleConfig)
}

const bgStyle = computed(() => {
  if (!props.coverUrl)
    return ''
  return `background: url(${props.coverUrl}) no-repeat; background-size: 100% auto; background-position: center top;`
})

// 当前章节
const currentChapter = computed(() => {
  const chapters = props.book?.chapters || []
  if (!chapters.length)
    return null
  const index = chapters.findIndex((c: any) => c.chapterId === props.book?.currentChapterId)
  const chapter = index !== -1 ? chapters[index] : chapters[0]
  return { ...chapter, index: index !== -1 ? index : 0 }
})

// 预加载 Unity WebGL 资源文件，各资源并发加载、各自立即缓存
// baseUrl 使用 fetch response.url（跟随重定向后的实际地址），确保解析出的绝对 URL 与 Unity Loader 一致
function preloadUnityAssets(baseUrl: string, paths: string[]): Promise<PromiseSettledResult<Response>[]> {
  return Promise.allSettled(
    paths.map((relativePath) => {
      const absoluteUrl = new URL(relativePath, baseUrl).href
      return fetch(absoluteUrl, { cache: 'force-cache' })
    }),
  )
}

onMounted(async () => {
  const preloadStart = performance.now()
  try {
    const response = await fetch('/content/read/index.html')
    if (!response.ok)
      return
    const htmlContent = await response.text()
    const unityFiles = htmlContent.match(/[^"]+?\.unityweb/g) || []
    if (unityFiles.length) {
      const results = await preloadUnityAssets(response.url, unityFiles)
      const allSucceeded = results.every(r => r.status === 'fulfilled' && r.value.ok)
      if (allSucceeded) {
        const costMs = Math.round(performance.now() - preloadStart)
        emit('preload-complete', costMs)
      }
    }
  }
  catch {
    // 预加载失败不影响主流程
  }
})

onMounted(() => {
  if (props.coverUrl) {
    const img = new Image()
    img.onload = () => emit('cover-loaded')
    img.src = props.coverUrl
  }
})
</script>

<template>
  <div class="min-h-screen" style="background: #F6F7F9;">
    <div
      class="absolute top-12px left-12px z-10 cursor-pointer"
      @click="emit('back')"
    >
      <i-icon-park-outline-left class="text-24px text-white" />
    </div>

    <div class="book-bg absolute inset-0" :style="bgStyle" />
    <div class="book-mask absolute bottom-0 w-full z-10" />

    <!-- TOC 弹窗 -->
    <BizPopupInteractiveTOC
      v-if="book"
      v-model="popupTOCModel"
      :book="book"
      :lock-icon-color="config.theme.tocLockColor"
      :restart-mode="restartMode"
      :current-chapter-id="props.currentProgressChapterId"
      :unlocked-chapter-ids="props.unlockedChapterIds"
      @reset-chapter="onResetChapter"
      @reset-all="onResetAll"
    />

    <!-- 重置二次确认弹窗 -->
    <BizDialogResetConfirm
      v-model="confirmVisible"
      :chapter-id="resetChapterId"
      :chapter-no="resetChapterNo"
      @confirm="handleResetConfirm"
    />

    <BasicFixedBottom>
      <div class="px-24px pb-24px">
        <h3 class="text-20px lh-26px color-white">
          {{ props.book.contentName }}
        </h3>
        <div class="flex justify-between items-center mt-12px color-#CFD0D6">
          <p class="flex flex-1 items-center text-12px font-300 min-w-0">
            <span class="shrink-0">Chapter {{ currentChapter?.index + 1 }}/{{ props.book.chapters.length }}</span>
            <span class="mx-8px color-#676566 shrink-0">|</span>
            <span class="truncate pr-16px">{{ currentChapter?.chapterName }}</span>
          </p>
        </div>
        <div class="chapter-brief max-h-138px overflow-y-auto mt-8px">
          <p class="lh-18px text-12px color-#CFD0D6 font-300">
            {{ currentChapter?.chapterBrief }}
          </p>
        </div>
        <div v-if="props.book.tagList?.length" class="flex flex-wrap text-12px font-300 color-#fff mt-10px mb--8px">
          <span v-for="tag in props.book.tagList" :key="tag.name" class="px-9px py-3px b-rd-16px bg-[rgba(198,201,236,0.2)] mr-8px mb-8px" @click="emit('goto-tags', tag.id)">
            {{ tag.name }}
          </span>
        </div>
      </div>
      <div class="flex w-full px-24px pt-12px pb-2">
        <span class="icon-btn" @click="openRestart">
          <img src="/images/vowpath/coins/reset-detail.webp" class="w-42px h-42px">
        </span>
        <a
          class="start-reading icon-btn flex-1 ml-20px block h-42px lh-42px text-center text-15px font-bold rounded-22.5px cursor-pointer btn-solid text-white"
          @click="emit('read')"
        >
          Reading
        </a>
      </div>
    </BasicFixedBottom>
  </div>
</template>

<style lang="scss" scoped>
.book-mask {
  height: 38%;
  background: linear-gradient(rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 1) 42%);
  pointer-events: none;
}
.start-reading {
  background-image: linear-gradient(100deg, #6417FF 0%, #F150FF 37%, #F150FF 70%, #FFECA8 100%);
}
.icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: opacity 0.15s, transform 0.15s;
  &:active {
    opacity: 0.6;
    transform: scale(0.9);
  }
}
.chapter-brief {
  &::-webkit-scrollbar {
    width: 3px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 3px;
  }
}
</style>
